import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import config from '@/config';
import { equipmentTypeEditState } from '@/state';
import { useLoadableSetAtom } from '@/state/loadableHooks';
import logger from '@/util/logger';
import {
    type EquipmentType,
    type EquipmentTypeId,
    type PurchaseLink,
    PurchaseLinkSchema,
    RETAILER_IDS,
    type RetailerId,
} from '@rock/types';
import { RETAILERS, type RetailerMeta, retailerLogoUrl } from '@rock/util';
import { type FC, useState } from 'react';
import { toast } from 'sonner';

const log = logger('PurchaseLinksSection');

/**
 * Read-only "Purchasing Links" section (shown above Specs for supplies-scoped types) with a
 * hover-revealed Edit button that opens a per-provider editor. Saves to the equipment-type draft;
 * the staging toolbar publishes. Self-hides for non-supplies types.
 */
export const PurchaseLinksSection: FC<{ typeId: EquipmentTypeId }> = ({ typeId }) => {
    const [loadable, updateEquipmentType] = useLoadableSetAtom(equipmentTypeEditState(typeId));
    const [editing, setEditing] = useState(false);
    const type = loadable.state === 'hasData' ? loadable.data : undefined;

    if (type?.scope !== 'supplies') {
        return null;
    }

    const links = type.purchaseLinks ?? [];

    const handleSave = async (purchaseLinks: PurchaseLink[]) => {
        try {
            await updateEquipmentType({ ...type, purchaseLinks });
            toast.success('Purchase links updated');
            log.info('Updated purchase links', { typeId, count: purchaseLinks.length });
            setEditing(false);
        } catch (err) {
            log.error('Failed to update purchase links', { err });
            toast.error('Failed to save purchase links');
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex flex-row justify-end">
                <Button onClick={() => setEditing(true)} variant="outline" size="sm" className="cursor-pointer">
                    Edit
                </Button>
            </div>

            {links.length === 0 ? (
                <div className="text-gray-500">No purchase links yet.</div>
            ) : (
                <div className="flex flex-col gap-3">
                    {links.map((link) => (
                        <PurchaseLinkRow key={link.provider} link={link} />
                    ))}
                </div>
            )}

            {editing && <PurchaseLinksDialog type={type} onSave={handleSave} onCancel={() => setEditing(false)} />}
        </div>
    );
};

const PurchaseLinkRow: FC<{ link: PurchaseLink }> = ({ link }) => {
    const retailer = RETAILERS[link.provider];
    return (
        <div className="flex flex-row items-center gap-3">
            <RetailerLogo retailer={retailer} />
            <div className="flex flex-col">
                <span className="font-medium">{retailer.name}</span>
                {link.kind === 'search' ? (
                    <span className="text-sm text-gray-500">
                        Search: <code>{link.query}</code>
                    </span>
                ) : (
                    <span className="text-sm text-gray-500 break-all">{link.url}</span>
                )}
            </div>
        </div>
    );
};

type LinkMode = 'none' | PurchaseLink['kind'];
type EditorRow = { mode: LinkMode; value: string };

const PurchaseLinksDialog: FC<{
    type: EquipmentType;
    onSave: (links: PurchaseLink[]) => Promise<void>;
    onCancel: () => void;
}> = ({ type, onSave, onCancel }) => {
    const [rows, setRows] = useState<Record<RetailerId, EditorRow>>(() => initRows(type.purchaseLinks ?? []));
    const [saving, setSaving] = useState(false);

    const setRow = (provider: RetailerId, patch: Partial<EditorRow>) =>
        setRows((prev) => ({ ...prev, [provider]: { ...prev[provider], ...patch } }));

    const handleSave = async () => {
        const links: PurchaseLink[] = [];
        for (const provider of RETAILER_IDS) {
            const row = rows[provider];
            if (row.mode === 'none') {
                continue;
            }
            const candidate =
                row.mode === 'search'
                    ? { provider, kind: 'search', query: row.value.trim() }
                    : { provider, kind: 'direct', url: row.value.trim() };
            const parsed = PurchaseLinkSchema.safeParse(candidate);
            if (!parsed.success) {
                toast.error(`${RETAILERS[provider].name}: ${parsed.error.issues[0]?.message ?? 'invalid link'}`);
                return;
            }
            links.push(parsed.data);
        }
        setSaving(true);
        // `onSave` swallows its own errors (toasts), so this never rejects; the finally re-enables the
        // dialog on failure (on success the parent closes it, making the reset a harmless no-op).
        try {
            await onSave(links);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onCancel()}>
            <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Purchasing Links — {type.title}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-4">
                    {RETAILER_IDS.map((provider) => {
                        const retailer = RETAILERS[provider];
                        const row = rows[provider];
                        return (
                            <div key={provider} className="flex flex-col gap-2 border-b pb-4 last:border-b-0">
                                <div className="flex flex-row items-center gap-2">
                                    <RetailerLogo retailer={retailer} />
                                    <span className="font-medium">{retailer.name}</span>
                                </div>
                                <div className="flex flex-row items-center gap-2">
                                    <Select
                                        value={row.mode}
                                        onValueChange={(v) => setRow(provider, { mode: toLinkMode(v) })}
                                    >
                                        <SelectTrigger className="bg-white w-40">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None</SelectItem>
                                            <SelectItem value="search">Search</SelectItem>
                                            <SelectItem value="direct">Direct</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {row.mode !== 'none' && (
                                        <Input
                                            className="bg-white flex-1"
                                            value={row.value}
                                            placeholder={
                                                row.mode === 'search'
                                                    ? '{size} air filter'
                                                    : `https://www.${retailer.domain}/...`
                                            }
                                            onChange={(e) => setRow(provider, { value: e.target.value })}
                                        />
                                    )}
                                </div>
                                {row.mode === 'search' && (
                                    <span className="text-xs text-gray-400">
                                        Use {'{dataKey}'} to insert a field value (e.g. {'{size}'}).
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
                <DialogFooter>
                    <Button variant="secondary" onClick={onCancel} disabled={saving} type="button">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving} type="button">
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/** Brandfetch logo for a retailer, falling back to a brand-colored initial when none is available. */
const RetailerLogo: FC<{ retailer: RetailerMeta }> = ({ retailer }) => {
    const url = retailerLogoUrl(retailer.domain, config.commerce.brandfetchClientId);
    const [failed, setFailed] = useState(false);

    if (!url || failed) {
        return (
            <div
                className="h-8 w-8 rounded border flex items-center justify-center font-bold text-white"
                style={{ backgroundColor: retailer.brandColor }}
            >
                {retailer.name.charAt(0)}
            </div>
        );
    }
    return (
        <img
            src={url}
            alt={`${retailer.name} logo`}
            className="h-8 w-8 rounded border bg-white object-contain"
            onError={() => setFailed(true)}
        />
    );
};

function initRows(links: PurchaseLink[]): Record<RetailerId, EditorRow> {
    const rows = {} as Record<RetailerId, EditorRow>;
    for (const provider of RETAILER_IDS) {
        const existing = links.find((link) => link.provider === provider);
        if (!existing) {
            rows[provider] = { mode: 'none', value: '' };
        } else if (existing.kind === 'search') {
            rows[provider] = { mode: 'search', value: existing.query };
        } else {
            rows[provider] = { mode: 'direct', value: existing.url };
        }
    }
    return rows;
}

function toLinkMode(value: string): LinkMode {
    if (value === 'search') {
        return 'search';
    }
    if (value === 'direct') {
        return 'direct';
    }
    return 'none';
}
